import * as Yup from "yup";
import { FormField } from "@/components/common/form-field";
import { FIELD_LABEL } from "@/features/clients/components/profile-form-shared";
import { CheckboxField } from "@/features/clients/components/record-form-fields";
import { taxDebtsApi } from "@/features/worksheets/api";
import { CrudDialog } from "@/features/worksheets/components/crud-dialog";
import { MoneyField } from "@/features/worksheets/components/money-field";
import { centsToDollars, dollarsToCents } from "@/features/worksheets/money";
import type { TaxDebt } from "@/features/worksheets/types";

interface TaxDebtFormValues {
  taxYear: string;
  amountOwed: string;
  csed: string;
  included: boolean;
}

const validationSchema = Yup.object({
  taxYear: Yup.number()
    .typeError("Enter a tax year")
    .integer("Enter a valid year")
    .min(1900, "Year must be 1900 or later")
    .max(2100, "Year must be 2100 or earlier")
    .required("Tax year is required"),
});

function toInitialValues(record: TaxDebt | null): TaxDebtFormValues {
  return {
    taxYear: record ? String(record.taxYear) : "",
    amountOwed: record ? centsToDollars(record.amountOwed) : "",
    csed: record?.csed ?? "",
    included: record?.included ?? true,
  };
}

function toPayload(v: TaxDebtFormValues): Record<string, unknown> {
  return {
    taxYear: Number(v.taxYear),
    amountOwed: dollarsToCents(v.amountOwed),
    csed: v.csed || null,
    included: v.included,
  };
}

export function TaxDebtDialog({
  caseId,
  record,
  open,
  onOpenChange,
  onSaved,
}: {
  caseId: string;
  record: TaxDebt | null;
  open: boolean;
  onOpenChange: (o: boolean) => void;
  onSaved: () => void;
}) {
  async function onSave(values: TaxDebtFormValues) {
    const payload = toPayload(values);
    if (record) {
      await taxDebtsApi.update(caseId, record.id, payload);
    } else {
      await taxDebtsApi.create(caseId, payload);
    }
  }

  return (
    <CrudDialog
      open={open}
      onOpenChange={onOpenChange}
      editing={record !== null}
      noun="Tax Year"
      initialValues={toInitialValues(record)}
      validationSchema={validationSchema}
      onSave={onSave}
      onSaved={onSaved}
    >
      <div className="grid grid-cols-2 gap-4">
        <FormField
          name="taxYear"
          label="Tax Year"
          labelClassName={FIELD_LABEL}
          inputMode="numeric"
          placeholder="e.g. 2020"
        />
        <MoneyField name="amountOwed" label="Amount Owed" />
      </div>
      <FormField name="csed" label="CSED" labelClassName={FIELD_LABEL} type="date" />
      <CheckboxField name="included" label="Include in IA/offer" />
    </CrudDialog>
  );
}
